import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import {
  isTokenExpired,
  getTokenAuthorities,
  getTokenUserId,
} from "../utils/tokenUtils";
import { clearAll as clearConsultSuppression } from "../utils/consultSuppression";
import {
  derivePharmacistPersona,
  derivePersonaKey,
} from "../utils/pharmacistPersona";
import { RootState } from "./index";
import { MarketplaceResponse } from "../types/shared";

type userInfoType = {
  id: string;
  username: string;
  firstName: string;
  secondName: string;
  roles: string[];
};

export interface AuthState {
  token: string | null;
  loginError: boolean;
  loadingLogin: boolean;
  loginServerError: boolean;

  userInfo: userInfoType | null;
  loadingUserInfo: boolean;
  userInfoError: boolean;

  marketplaceInfo: any;
  loadingMarketplaceInfo: boolean;
  marketplaceInfoError: boolean;

  marketplaceInfoDetailed: MarketplaceResponse | null;
  loadingMarketplaceInfoDetailed: boolean;
  marketplaceInfoDetailedError: boolean;

  loadingForgetPassword: boolean;
  forgetPasswordError: boolean;

  isAdmin: boolean;
  isPharmacist: boolean;
  isDriver: boolean;
  isInitialLogin: boolean;
  authInitialized: boolean;
  isMarketplaceAdmin: boolean;
  /** ITAM-2622: the consult-only PHARMACIST_USER persona. */
  isPharmacistConsultant: boolean;
}

const initialState: AuthState = {
  token: null,
  loginError: false,
  loadingLogin: false,
  loginServerError: false,

  userInfo: {
    id: "",
    username: "",
    firstName: "",
    secondName: "",
    roles: [],
  },
  loadingUserInfo: false,
  userInfoError: false,

  marketplaceInfo: null,
  loadingMarketplaceInfo: false,
  marketplaceInfoError: false,

  marketplaceInfoDetailed: null,
  loadingMarketplaceInfoDetailed: false,
  marketplaceInfoDetailedError: false,

  loadingForgetPassword: false,
  forgetPasswordError: false,

  isAdmin: false,
  isMarketplaceAdmin: false,
  isPharmacist: false,
  isDriver: false,
  isPharmacistConsultant: false,
  isInitialLogin: false,
  authInitialized: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState: initialState,
  reducers: {
    loginStart: (state) => {
      state.loadingLogin = true;
    },
    userInfoFn: (state, action: PayloadAction<userInfoType | null>) => {
      state.userInfo = action.payload;
      state.loadingUserInfo = false;
      state.userInfoError = false;
    },
    fetchUserInfoStart: (state) => {
      state.loadingUserInfo = true;
      state.userInfoError = false;
    },
    fetchUserInfoFailed: (state) => {
      state.loadingUserInfo = false;
      state.loadingLogin = false;
      state.userInfoError = true;
    },

    loginSuccess: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
      state.loadingLogin = false;
      state.loginError = false;
    },
    loginFailed: (state) => {
      state.loadingLogin = false;
      state.loginError = true;
    },
    loginStatusReset: (state) => {
      state.loginError = false;
      state.loadingLogin = false;
      state.loginServerError = false;
    },
    logout: (state) => {
      // Clear all state first
      state.token = null;
      state.marketplaceInfo = null;
      state.marketplaceInfoDetailed = null;
      state.userInfo = {
        id: "",
        username: "",
        firstName: "",
        secondName: "",
        roles: [],
      };

      // Reset all role flags
      state.isAdmin = false;
      state.isMarketplaceAdmin = false;
      state.isPharmacist = false;
      state.isDriver = false;
      state.isPharmacistConsultant = false;
      state.isInitialLogin = false;

      // Reset loading and error states
      state.loginError = false;
      state.loadingLogin = false;

      // Keep authInitialized true for subsequent logins
      state.authInitialized = true;

      // Clear localStorage after state
      clearLocalStorage();

      console.log("Auth: Complete logout performed");
    },
    fetchMarketplaceInfoStart: (state) => {
      state.loadingMarketplaceInfo = true;
      state.marketplaceInfoError = false;
    },
    marketplaceInfo: (state, action) => {
      state.marketplaceInfo = action.payload;
      state.loadingMarketplaceInfo = false;
      state.marketplaceInfoError = false;
    },
    fetchMarketplaceInfoFailed: (state, action) => {
      state.marketplaceInfoError = action.payload;
      state.loadingMarketplaceInfo = false;
      state.loadingLogin = false;
      state.marketplaceInfoError = true;
    },
    loginServerError: (state) => {
      state.loginError = false;
      state.loginServerError = true;
      state.loadingLogin = false;
    },
    setActiveStatus: (state, action) => {
      if (state.marketplaceInfo) {
        state.marketplaceInfo = {
          ...state.marketplaceInfo,
          takesOrders: action.payload,
        };
      }
    },
    fetchMarketplaceInfoDetailedStart: (state) => {
      state.loadingMarketplaceInfoDetailed = true;
      state.marketplaceInfoDetailedError = false;
    },
    setMarketplaceInfoDetailed: (
      state,
      action: PayloadAction<MarketplaceResponse>
    ) => {
      state.marketplaceInfoDetailed = action.payload;
      state.loadingMarketplaceInfoDetailed = false;
      state.marketplaceInfoDetailedError = false;
    },
    fetchMarketplaceInfoDetailedFailed: (state) => {
      state.loadingMarketplaceInfoDetailed = false;
      state.marketplaceInfoDetailedError = true;
    },
    setIsMarketplaceAdmin: (state) => {
      state.isMarketplaceAdmin = true;
      state.isAdmin = false;
      state.isPharmacist = false;
      state.isDriver = false;
      state.isPharmacistConsultant = false;
    },
    setIsAdmin: (state) => {
      state.isAdmin = true;
      state.isMarketplaceAdmin = false;
      state.isPharmacist = false;
      state.isDriver = false;
      state.isPharmacistConsultant = false;
    },
    setIsPharmacist: (state) => {
      state.isAdmin = false;
      state.isMarketplaceAdmin = false;
      state.isPharmacist = true;
      state.isDriver = false;
      state.isPharmacistConsultant = false;
    },
    setIsDriver: (state) => {
      state.isDriver = true;
      state.isAdmin = false;
      state.isMarketplaceAdmin = false;
      state.isPharmacist = false;
      state.isPharmacistConsultant = false;
    },
    setRegularUser: (state) => {
      state.isAdmin = false;
      state.isMarketplaceAdmin = false;
      state.isPharmacist = false;
      state.isDriver = false;
      state.isPharmacistConsultant = false;
    },
    // ITAM-2622: the consult-only pharmacist persona. Mutually exclusive with
    // the five above by construction, exactly like they are with each other.
    setIsPharmacistConsultant: (state) => {
      state.isPharmacistConsultant = true;
      state.isAdmin = false;
      state.isMarketplaceAdmin = false;
      state.isPharmacist = false;
      state.isDriver = false;
    },
    /**
     * ITAM-2622 (3.2/3.6): dispatched when a session changes persona WITHOUT
     * logging out — a token refresh that adds or drops a persona-deciding role.
     * Every non-auth slice resets on this action type as well as on
     * "auth/logout", because a refresh dispatches `loginSuccess`, never
     * `auth/logout`, so without it patient PII from the previous persona would
     * survive into the new one.
     *
     * Marketplace state is cleared here too: after a persona change the old
     * pair is stale by definition. A refresh that lands on a retail branch
     * refetches it immediately; one that lands on a skip-marketplace branch
     * would otherwise keep serving the previous persona's ids.
     */
    personaExit: (state) => {
      state.marketplaceInfo = null;
      state.marketplaceInfoDetailed = null;
      localStorage.removeItem("marketplaceId");
      localStorage.removeItem("branchMarketplaceId");
    },
    setIsInitialLogin: (state, action: PayloadAction<boolean>) => {
      state.isInitialLogin = action.payload;
    },
    setAuthInitialized: (state, action: PayloadAction<boolean>) => {
      state.authInitialized = action.payload;
    },
  },
});

const clearLocalStorage = () => {
  // Clear all authentication-related keys
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("expirationDate");
  localStorage.removeItem("email");
  localStorage.removeItem("id");
  localStorage.removeItem("firstName");
  localStorage.removeItem("secondName");
  localStorage.removeItem("roles");

  // Clear marketplace IDs
  localStorage.removeItem("marketplaceId");
  localStorage.removeItem("branchMarketplaceId");

  // Video-consult PiP: drop the joined-consult suppression markers. Must be
  // synchronous — the 401/403 interceptor hard-redirects on the line after
  // its logout dispatch, so cleanup after a task-queue hop would be lost.
  clearConsultSuppression();

  // Clear any other potential cached marketplace data
  // You might need to add other keys if you discover them
  localStorage.removeItem("selectedMarketplace");
  localStorage.removeItem("marketplaceCache");

  // Log cleanup for debugging
  console.log("Auth: localStorage cleared completely");

  // Optional - more aggressive cleanup of all apoteka-related keys
  // Uncomment if you want to clear ALL localStorage keys that might be related
  /*
  Object.keys(localStorage).forEach(key => {
    if (key.includes('marketplace') || key.includes('branch') || key.includes('apoteka')) {
      localStorage.removeItem(key);
      console.log(`Cleared additional localStorage key: ${key}`);
    }
  });
  */
};

export const authActions = authSlice.actions;

// Memoized selector for checking if user is authenticated with valid token
export const selectIsAuthenticated = createSelector(
  [
    (state: RootState) => state.auth.token,
    (state: RootState) => state.auth.authInitialized,
  ],
  (token, isInitialized) => {
    // Return false if no token or auth not initialized
    if (!token || !isInitialized) {
      return false;
    }

    // Validate token expiration
    try {
      return !isTokenExpired(token);
    } catch {
      console.warn("Auth: Invalid token format during validation");
      return false;
    }
  }
);

// Extract user roles from token using robust tokenUtils
export const selectUserRoles = createSelector(
  [selectIsAuthenticated, (state: RootState) => state.auth.token],
  (isAuthenticated, token) => {
    if (!isAuthenticated || !token) {
      return [];
    }
    return getTokenAuthorities(token);
  }
);

// ITAM-2622 role predicates. All of them delegate to the one pure helper, so
// the auth thunks (which only have a raw role array at branch time) and these
// selectors can never disagree about who is a pharmacist consultant.
export const selectHasPharmacistConsultantRole = createSelector(
  [selectUserRoles],
  (roles) => derivePharmacistPersona(roles).hasPharmacistRole
);

export const selectHasRetailRole = createSelector(
  [selectUserRoles],
  (roles) => derivePharmacistPersona(roles).hasRetailRole
);

/**
 * Gates EVERY consult surface — never the raw role. An `ADMIN +
 * PHARMACIST_USER` token keeps the admin persona with no pharmacist side
 * effects (decision 6).
 */
export const selectHasSupportedPharmacistCapability = createSelector(
  [selectUserRoles],
  (roles) => derivePharmacistPersona(roles).hasSupportedPharmacistCapability
);

export const selectIsConsultOnlySession = createSelector(
  [selectUserRoles, (state: RootState) => state.auth.isPharmacistConsultant],
  (roles, flag) => flag || derivePharmacistPersona(roles).isConsultOnly
);

export const selectIsDualRetailPharmacist = createSelector(
  [selectUserRoles],
  (roles) => derivePharmacistPersona(roles).isDualRetail
);

/**
 * Scalar remount key for `ConsultOrderManager` (3.6). Must stay a scalar:
 * interpolating the persona OBJECT yields "42:[object Object]", a key that
 * never changes and silently defeats the teardown.
 */
export const selectPersonaKey = createSelector([selectUserRoles], (roles) =>
  derivePersonaKey(roles)
);

export const selectTokenUserId = createSelector(
  [selectIsAuthenticated, (state: RootState) => state.auth.token],
  (isAuthenticated, token) =>
    isAuthenticated && token ? getTokenUserId(token) : null
);

// ITAM-2622 (3.5): consultation history is a pharmacist-capability surface, not
// a retail one. Each account sees only its own consults (decision 13).
export const selectCanViewTransactions = selectHasSupportedPharmacistCapability;

// Enhanced role selectors that check both Redux state and token roles
export const selectIsAdmin = createSelector(
  [selectUserRoles, (state: RootState) => state.auth.isAdmin],
  (roles, isAdminFlag) => {
    return isAdminFlag || roles.includes("ADMIN");
  }
);

export const selectIsMarketplaceAdmin = createSelector(
  [selectUserRoles, (state: RootState) => state.auth.isMarketplaceAdmin],
  (roles, flag) => flag || roles.includes("MARKETPLACE_ADMIN_USER")
);

export const selectCanViewAdminMarketplace = createSelector(
  [selectIsMarketplaceAdmin],
  (isMarketplaceAdmin) => isMarketplaceAdmin
);

export const selectIsPharmacist = createSelector(
  [selectUserRoles, (state: RootState) => state.auth.isPharmacist],
  (roles, isPharmacistFlag) => {
    return (
      isPharmacistFlag ||
      roles.includes("PHARMACIST_SUPPORT_USER") ||
      roles.includes("PHARMACIST")
    );
  }
);



export const selectIsDriver = createSelector(
  [selectUserRoles, (state: RootState) => state.auth.isDriver],
  (roles, isDriverFlag) => {
    // The backend auto-grants DELIVERY_USER to customer accounts on delivery
    // completion, so the raw role is driver evidence only when no supported
    // pharmacist capability outranks it (mirrors resolveSessionPersona).
    return (
      isDriverFlag ||
      (roles.includes("DELIVERY_USER") &&
        !derivePharmacistPersona(roles).hasSupportedPharmacistCapability)
    );
  }
);

// Determine user type based on roles
export const selectUserType = createSelector(
  [
    selectIsAdmin,
    selectIsPharmacist,
    selectIsDriver,
    selectIsMarketplaceAdmin,
    selectIsConsultOnlySession,
  ],
  (isAdmin, isPharmacist, isDriver, isMarketplaceAdmin, isConsultOnly) => {
    if (isAdmin) return "admin";
    if (isMarketplaceAdmin) return "marketplaceAdmin";
    if (isPharmacist) return "pharmacist";
    if (isDriver) return "driver";
    if (isConsultOnly) return "pharmacistConsultant";
    return "marketplace";
  }
);

// Comprehensive auth state selector
export const selectAuthState = createSelector(
  [
    selectIsAuthenticated,
    selectIsAdmin,
    selectIsPharmacist,
    selectIsDriver,
    selectUserType,
    selectUserRoles,
  ],
  (isAuthenticated, isAdmin, isPharmacist, isDriver, userType, roles) => ({
    isAuthenticated,
    isAdmin,
    isPharmacist,
    isDriver,
    userType,
    roles,
    canAccessAdminArea: isAuthenticated && isAdmin,
    canAccessPharmacyArea: isAuthenticated && (isPharmacist || isAdmin),
    canAccessDriverArea: isAuthenticated && (isDriver || isAdmin),
  })
);

// Selector for detailed marketplace info
export const selectMarketplaceDetailed = (state: RootState) =>
  state.auth.marketplaceInfoDetailed;

// Selector for active pharmacist license
export const selectActiveLicense = createSelector(
  [selectMarketplaceDetailed],
  (detailed) => detailed?.marketplacePharmacyLicenseResponse ?? null
);

export default authSlice;
